import React, { useState, useEffect, useCallback } from "react";
import { useLanguage } from "../contexts/LanguageContext.jsx";
import { t, normalizeLang } from "../utils/translations";
import { fmtTokenAmount, fmtEurPrice, fmtEurRow } from "../utils/formatNumbers";
import MaskedEurCell from "./MaskedEurCell";
import {
  backendCurrencyOrFallback,
  blockedTaxLabel,
  currencySymbolOf,
  isBlockedTaxRow,
  pickFiatBuy,
  pickFiatSell,
  pickFiatTotal,
  pickFiatTaxable,
  pickFiatGrossProfit,
  fiatColumnLabelFromEurKey,
} from "../utils/fiatFields";
import { isNftCostBasisUnknown, isNftKnownZeroCostBasis } from "../utils/nftCostBasis";
import { formatDateFromTimestamp, formatDateTimeUtcFromTimestamp } from "../utils/dateUtils";
import {
  isUkCountry,
  isUkNftIdentifiableRow,
} from "../utils/ukTaxUi.js";
import {
  resolveEffectiveCountry,
  getSaleDetailTitle,
  shouldUseUkAssignmentLabels,
} from "../utils/taxAssignmentLabels.js";

export default function SaleDetailRow({ 
  saleTx, 
  fullData,
  colSpan = 10,
  isEditMode = false,
  nonTaxableSales = {},
  onNonTaxableChange = () => {},
  selectedCountry = 'DE', // 🎯 NEU: Land für Steuerberechnung
  // 🔧 NEU: Callback für manuelle NFT-Eingaben
  onManualNftDataChange = () => {},
  // 🔧 NEU: Gespeicherte manuelle Daten
  manualNftData = {},
  isPreviewMode = false,
  maskRowEur = false,
  pdfExportMode = false,
  language: propLanguage,
}) {
  const { language: contextLanguage } = useLanguage();
  const language = normalizeLang(propLanguage ?? contextLanguage ?? "en");
  const showIcons = !pdfExportMode;

  const hasNumericFiatValue = (v) => {
    if (v === null || v === undefined || v === "") return false;
    const n = Number(v);
    return Number.isFinite(n);
  };

  const firstNumeric = (...values) => {
    for (const v of values) {
      if (v === null || v === undefined || v === "") continue;
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };

  const hasKnownOriginalPurchaseCost = (entry = {}) => {
    const cost = firstNumeric(
      entry.grossBuyFiat,
      entry.grossBuyEUR,
      entry.nftAcquisitionCostFiat,
      entry.nftAcquisitionCostEUR,
      entry.totalFiat,
      entry.totalEUR,
      entry.amountFiat,
      entry.euroAmount
    );

    if (cost === null) return false;
    if (cost !== 0) return true;

    return Boolean(
      entry.costBasisSource === "user_confirmed_zero_cost_basis" ||
        entry.costBasisSource === "verified_free_mint" ||
        entry.manualCostBasisOverride === true ||
        entry.acquisitionStatus === "paid_mint_tax_row"
    );
  };

  const originalPurchaseCost = (entry = {}) => {
    const direct = firstNumeric(
      entry.grossBuyFiat,
      entry.grossBuyEUR,
      entry.nftAcquisitionCostFiat,
      entry.nftAcquisitionCostEUR,
      entry.totalFiat,
      entry.totalEUR,
      entry.amountFiat,
      entry.euroAmount
    );

    if (direct !== null) return direct;

    const amount = firstNumeric(entry.amount);
    const price = firstNumeric(entry.unitPriceFiat, entry.euroPrice, entry.priceFiat);
    if (amount !== null && price !== null) return amount * price;

    return null;
  };
  
  const fiatCurrency = backendCurrencyOrFallback(selectedCountry, saleTx);
  const effectiveCountry = resolveEffectiveCountry(selectedCountry, saleTx);
  const saleLegs =
    saleTx?.fifoLegs ||
    saleTx?.matchedInflows ||
    fullData?.fifoLegs ||
    [];
  const isUK =
    isUkCountry(effectiveCountry) ||
    shouldUseUkAssignmentLabels(effectiveCountry, saleTx, saleLegs);
  const isUkNft = isUkNftIdentifiableRow(saleTx, effectiveCountry);
  const saleDetailTitle = getSaleDetailTitle(
    effectiveCountry,
    language,
    saleTx,
    saleLegs
  );

  // 🧪 Preview Mode (robust): falls Parent es nicht direkt durchreicht
  const previewMode = Boolean(isPreviewMode || fullData?.isPreviewMode || saleTx?.isPreviewMode);
  
  // ✅ Helper: Timestamp robust lesen (verhindert 1970-01-01)
  const pickTs = (x) => {
    const raw = x?.timestamp ?? x?.block_timestamp ?? x?.ts ?? x?.acquired_ts ?? x?.acq_ts ?? x?.created_at ?? x?.date ?? 0;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return n > 1e12 ? Math.floor(n / 1000) : n;
  };
  
  // 🔧 NEU: Unique key für State Management
  const saleKey = `${saleTx.hash || saleTx.timestamp}_${saleTx.nftName}`;
  
  // 🔧 NEU: Lokale State-Variablen mit Initialisierung aus gespeicherten Daten
  const [manualEinstand, setManualEinstand] = useState(
    manualNftData[saleKey]?.einstand || ""
  );
  const [manualKaufDatum, setManualKaufDatum] = useState(
    manualNftData[saleKey]?.kaufDatum || ""
  );
  const [isUnbekannt, setIsUnbekannt] = useState(
    manualNftData[saleKey]?.isUnbekannt || false
  );

  // 🔧 NEU: Verhindere Endlos-Loop durch Referenz-Stabilität
  const stableOnManualNftDataChange = useCallback((key, data) => {
    onManualNftDataChange(key, data);
  }, [onManualNftDataChange]);

  // 🔧 NEU: Aktualisiere lokale Werte nur beim ersten Laden oder wenn sich der Key ändert
  useEffect(() => {
    if (manualNftData[saleKey]) {
      setManualEinstand(manualNftData[saleKey].einstand || "");
      setManualKaufDatum(manualNftData[saleKey].kaufDatum || "");
      setIsUnbekannt(manualNftData[saleKey].isUnbekannt || false);
    }
  }, [saleKey, manualNftData]); // manualNftData als Dependency hinzugefügt

  // 🔧 NEU: Automatische Synchronisation der lokalen States mit der übergeordneten Komponente
  useEffect(() => {
    // Nur ausführen wenn wir im Bearbeitungsmodus sind UND es tatsächlich Änderungen gibt
    if (isEditMode && (manualEinstand || manualKaufDatum)) {
      // Prüfe ob sich die Daten tatsächlich geändert haben
      const currentSavedData = manualNftData[saleKey];
      const hasChanges = !currentSavedData || 
        currentSavedData.einstand !== manualEinstand ||
        currentSavedData.kaufDatum !== manualKaufDatum ||
        currentSavedData.isUnbekannt !== isUnbekannt;
      
      if (hasChanges) {
        const dataToSave = {
          einstand: manualEinstand,
          kaufDatum: manualKaufDatum,
          isUnbekannt,
          nftName: saleTx.nftName,
          tokenId: saleTx.tokenId || saleTx.token_id,
          timestamp: saleTx.timestamp
        };
        
        // Verzögerung um zu viele API-Calls zu vermeiden
        const timeoutId = setTimeout(() => {
          stableOnManualNftDataChange(saleKey, dataToSave);
        }, 500);
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, [manualEinstand, manualKaufDatum, isUnbekannt, isEditMode, saleKey, saleTx.nftName, saleTx.tokenId, saleTx.timestamp, stableOnManualNftDataChange, manualNftData]);

  // 🎯 BACKEND-FIRST: FIFO-Legs / Tax-Row vom Backend
  const fifoLegs = Array.isArray(saleTx?.fifoLegs) ? saleTx.fifoLegs : [];
  const hasBackend = fifoLegs.length > 0;

  const maskDetailEur = previewMode && maskRowEur;
  const maskSummaryEur = previewMode && maskRowEur;
  const nftCostUnknown =
    isNftCostBasisUnknown(saleTx) && !isNftKnownZeroCostBasis(saleTx);

  const txMarkedMissingCostBasis =
    nftCostUnknown ||
    Boolean(saleTx?.missingCostBasis ?? saleTx?.costBasisUnknown) ||
    String(saleTx?.pricingReason || "") === "NFT_ACQUISITION_COST_BASIS_UNKNOWN";

  const savedManualEinstandRaw = manualNftData[saleKey]?.einstand;
  const savedManualKaufDatumRaw = manualNftData[saleKey]?.kaufDatum;

  const hasSavedManualEinstand =
    savedManualEinstandRaw !== undefined &&
    savedManualEinstandRaw !== null &&
    savedManualEinstandRaw !== "";

  const hasSavedManualKaufDatum =
    savedManualKaufDatumRaw !== undefined &&
    savedManualKaufDatumRaw !== null &&
    savedManualKaufDatumRaw !== "";

  const hasCompleteManualBasis =
    hasSavedManualEinstand &&
    (selectedCountry === "GR" || isUK || hasSavedManualKaufDatum);

  // Missing-Cost-Basis ist auch ohne fifoLegs blocked; mit manuellem Einstand darf gerechnet werden
  const backendTaxBlocked =
    isBlockedTaxRow(saleTx) &&
    !hasCompleteManualBasis;

  const backendCostFiat = hasBackend && !nftCostUnknown
    ? (pickFiatBuy(saleTx) ??
        fifoLegs.reduce(
          (s, l) =>
            s +
            Number(l.qty || 0) *
              Number(l.unit_cost_fiat ?? l.unit_cost_eur ?? l.unit_cost ?? 0),
          0
        ))
    : (isNftKnownZeroCostBasis(saleTx) ? 0 : null);
  const backendSellFiat = hasBackend
    ? (pickFiatSell(saleTx) ?? pickFiatTotal(saleTx) ?? 0)
    : (pickFiatSell(saleTx) ?? pickFiatTotal(saleTx) ?? saleTx.euroAmount ?? saleTx.amountFiat ?? 0);
  const backendTaxable = hasBackend
    ? (hasNumericFiatValue(pickFiatTaxable(saleTx))
        ? Number(pickFiatTaxable(saleTx))
        : (
            hasNumericFiatValue(backendSellFiat) && hasNumericFiatValue(backendCostFiat)
              ? backendSellFiat - backendCostFiat
              : null
          ))
    : null;
  // ✅ FIX: isFullyTaxFree richtig interpretieren (basiert auf Legs)
  const backendIsTaxFree = hasBackend ? !!saleTx.isFullyTaxFree : false;
  const legTs = hasBackend
    ? fifoLegs.map(pickTs).filter(ts => ts > 0)
    : [];

  const purchaseTs = hasBackend
    ? (saleTx.purchaseTimestamp ?? (legTs.length ? Math.min(...legTs) : undefined))
    : undefined;
  const holdingDays = hasBackend && purchaseTs
    ? Math.floor(((saleTx.timestamp || 0) - purchaseTs) / 86400)
    : undefined;

  // 🔧 DEBUG: Backend-First NFT Sale Debugging
  useEffect(() => {
    if (hasBackend) {
          }
  }, [hasBackend, saleTx?.nftName, saleTx?.tokenId, saleTx?.fifoLegs?.length, backendCostFiat, backendSellFiat, backendTaxable, backendIsTaxFree]);

  if (!saleTx || !fullData || !saleTx.nftName || !saleTx.timestamp) {
    return (
      <tr className="text-sm bg-white text-center">
        <td colSpan={colSpan} className="p-2 border text-red-600">
          ❌ {t('noSaleDataAvailable', language)}
        </td>
      </tr>
    );
  }

  const txName = (saleTx.nftName || "").toLowerCase();
  const saleTimestamp = saleTx.timestamp;
  const saleTokenId = saleTx.tokenId || saleTx.token_id;

  // 🔧 WICHTIG: Exakte Token ID Übereinstimmung für korrekte Einstandsberechnung
  const originalPurchase = [...fullData]
    .filter(
      (entry) => {
        // 1. NFT-Name muss übereinstimmen
        const entryName = (entry.nftName || entry.name || "").toLowerCase();
        if (entryName !== txName) return false;
        
        // 2. Token ID muss exakt übereinstimmen (falls vorhanden)
        if (saleTokenId && entry.tokenId !== saleTokenId && entry.token_id !== saleTokenId) {
          return false;
        }
        
        // 3. Muss vor dem Verkauf passiert sein
        if (entry.timestamp >= saleTimestamp) return false;
        
        // 4. Muss ein Mint ODER ein Transfer-In sein
        return entry.type === "mint" || entry.type === "received";
      }
    )
    .sort((a, b) => b.timestamp - a.timestamp)[0];

  // 🔧 NEU: Prüfe ob es ein Transfer-In ohne Mint gibt
  const hasTransferIn = [...fullData].some(entry => {
    const entryName = (entry.nftName || entry.name || "").toLowerCase();
    if (entryName !== txName) return false;
    if (saleTokenId && entry.tokenId !== saleTokenId && entry.token_id !== saleTokenId) return false;
    if (entry.timestamp >= saleTimestamp) return false;
    return entry.type === "received";
  });

  // 🔧 NEU: Prüfe ob es ein Mint mit der gleichen Token ID gibt
  const hasMint = [...fullData].some(entry => {
    const entryName = (entry.nftName || entry.name || "").toLowerCase();
    if (entryName !== txName) return false;
    if (saleTokenId && entry.tokenId !== saleTokenId && entry.token_id !== saleTokenId) return false;
    if (entry.timestamp >= saleTimestamp) return false;
    return entry.type === "mint";
  });

  // 🔧 NEU: Bestimme den Einstandstyp
  let einstandType = "unknown";
  let einstandSource = null;
  let warningMessage = "";

  if (originalPurchase) {
    if (originalPurchase.type === "mint") {
      einstandType = "purchase";
      einstandSource = originalPurchase;
    } else if (originalPurchase.type === "received") {
      einstandType = "transfer_in";
      einstandSource = originalPurchase;
      warningMessage = pdfExportMode ? "ACHTUNG: Kein NFT-Kauf erkannt! Einstandspreis und Kaufdatum müssen manuell ergänzt werden." : "⚠️ ACHTUNG: Kein NFT-Kauf erkannt! Einstandspreis und Kaufdatum müssen manuell ergänzt werden.";
    }
  } else if (hasTransferIn && !hasMint) {
    // 🔧 NEU: Geschenktes NFT ohne bekannten Einstandspreis
    einstandType = "gift";
    warningMessage = pdfExportMode ? "NFT wurde geschenkt - Einstandspreis und Kaufdatum unbekannt" : "🎁 NFT wurde geschenkt - Einstandspreis und Kaufdatum unbekannt";
  } else {
    // 🔧 NEU: Keine Quelle gefunden
    einstandType = "no_source";
    warningMessage = pdfExportMode ? "NFT Kauf nicht vorhanden - manuelle Eingabe erforderlich" : "❌ NFT Kauf nicht vorhanden - manuelle Eingabe erforderlich";
  }

  // 🔧 NEU: Einstandsdaten basierend auf Typ UND manuellen Eingaben
  let kaufDatum = null;
  let tage = null;
  let einstandEUR = null;

  if (einstandType === "purchase" && einstandSource) {
    // Normaler Kauf - nur mit belastbarem Einstand (Raw-Mint ohne Zahlung ≠ 0)
    kaufDatum = new Date(einstandSource.timestamp * 1000);
    const verkaufsDatum = new Date(saleTimestamp * 1000);
    tage = Math.floor((verkaufsDatum - kaufDatum) / (1000 * 60 * 60 * 24));
    
    einstandEUR = originalPurchaseCost(einstandSource);
  } else if (einstandType === "transfer_in" && einstandSource) {
    // Kein automatisches Anschaffungsdatum aus reinem Wallet-Empfang ableiten
    kaufDatum = null;
    tage = null;
    einstandEUR = 0;
  } else if (einstandType === "gift") {
    // Geschenk / externer Transfer:
    // ohne belastbare Anschaffungsquelle weder Kaufdatum noch Einstand automatisch setzen
    kaufDatum = null;
    tage = null;
    einstandEUR = 0;
  }

  // 🔧 NEU: Verwende manuelle Daten falls vorhanden
  const isManuallyNonTaxable = nonTaxableSales[saleKey];

  // 🔧 NEU: Handler für Checkbox-Änderungen
  const handleNonTaxableChange = (checked) => {
    onNonTaxableChange(saleKey, checked);
  };

  const manualEinstandRaw = savedManualEinstandRaw;
  const hasManualEinstand = hasSavedManualEinstand;

  const manualKaufDatumRaw = savedManualKaufDatumRaw;
  const hasManualKaufDatum = hasSavedManualKaufDatum;

  const txMarkedMissingPrice = Boolean(
    saleTx?.pricingMissing ??
    saleTx?.priceMissing ??
    saleTx?.missingFiatPrice
  );

  const legsHaveMissingCost =
    hasBackend &&
    fifoLegs.some((l) => {
      const explicitUnknown = Boolean(l?.costBasisUnknown || l?.cost_basis_unknown);
      const hasUnitCost = hasNumericFiatValue(
        l?.unit_cost_fiat ?? l?.unit_cost_eur ?? l?.unit_cost
      );
      const hasBuyValue = hasNumericFiatValue(l?.buyFiat ?? l?.buyEUR);
      return explicitUnknown || (!hasUnitCost && !hasBuyValue);
    });

  const backendEinstandKnown =
    hasBackend &&
    !txMarkedMissingCostBasis &&
    !legsHaveMissingCost &&
    (
      hasNumericFiatValue(saleTx?.grossBuyFiat ?? saleTx?.grossBuyEUR) ||
      fifoLegs.length > 0
    );

  const autoEinstandKnown =
    !hasBackend &&
    einstandType === "purchase" &&
    !!einstandSource &&
    hasKnownOriginalPurchaseCost(einstandSource) &&
    !txMarkedMissingCostBasis;

  const displayEinstandKnown =
    hasManualEinstand ||
    backendEinstandKnown ||
    autoEinstandKnown;

  const saleValueKnown = hasBackend
    ? (
        hasNumericFiatValue(saleTx?.grossSellFiat ?? saleTx?.grossSellEUR) ||
        hasNumericFiatValue(saleTx?.amountFiat ?? saleTx?.euroAmount)
      )
    : hasNumericFiatValue(saleTx?.amountFiat ?? saleTx?.euroAmount);

  const shouldShowInputFields =
    isEditMode &&
    !previewMode &&
    (
      txMarkedMissingCostBasis ||
      einstandType === "transfer_in" ||
      einstandType === "gift" ||
      einstandType === "no_source"
    );

  const displayKaufDatum = manualKaufDatumRaw
    ? manualKaufDatumRaw
    : (hasBackend && purchaseTs
        ? new Date(purchaseTs * 1000)
        : (autoEinstandKnown && kaufDatum ? kaufDatum : null));

  const effectiveHoldingDays = manualKaufDatumRaw
    ? Math.floor((new Date(saleTimestamp * 1000) - new Date(manualKaufDatumRaw)) / (1000 * 60 * 60 * 24))
    : (hasBackend && holdingDays !== undefined
        ? holdingDays
        : (autoEinstandKnown && tage !== null ? tage : null));

  const finalHaltedauerStr = isUK
    ? (language === "en" ? "Identifiable NFT" : "Identifiable NFT")
    : effectiveHoldingDays !== null && effectiveHoldingDays !== undefined
      ? `${effectiveHoldingDays} Tage`
      : "—";

  const finalEinstandEUR = hasBackend
    ? (backendEinstandKnown
        ? backendCostFiat
        : (hasManualEinstand ? Number(manualEinstandRaw || 0) : null))
    : (hasManualEinstand
        ? Number(manualEinstandRaw || 0)
        : (autoEinstandKnown ? einstandEUR : null));

  const finalSaleEUR = hasBackend
    ? backendSellFiat
    : ((saleTx.amountFiat ?? saleTx.euroAmount) || 0);

  const grossProfitValue = backendTaxBlocked
    ? null
    : hasBackend
    ? (
        !txMarkedMissingCostBasis &&
        hasNumericFiatValue(pickFiatGrossProfit(saleTx))
          ? Number(pickFiatGrossProfit(saleTx))
          : (
              !txMarkedMissingCostBasis &&
              backendEinstandKnown &&
              saleValueKnown
                ? (backendSellFiat - backendCostFiat)
                : null
            )
      )
    : (
        hasManualEinstand && saleValueKnown
          ? (finalSaleEUR - Number(manualEinstandRaw || 0))
          : (
              autoEinstandKnown && saleValueKnown
                ? (finalSaleEUR - einstandEUR)
                : null
            )
      );

  const taxStatusKnown = hasBackend
    ? !txMarkedMissingCostBasis && !backendTaxBlocked
    : (
        autoEinstandKnown ||
        (
          hasManualEinstand &&
          (selectedCountry === 'GR' || hasManualKaufDatum)
        )
      );

  const finalSteuerpflichtig = !taxStatusKnown
    ? null
    : (
        hasBackend
          ? (isUK ? true : !backendIsTaxFree)
          : (
              isUK || selectedCountry === "GR"
                ? true
                : (isManuallyNonTaxable ? false : Number(effectiveHoldingDays) < 365)
            )
      );

  const finalSteuerGewinnEUR = backendTaxBlocked
    ? null
    : hasBackend
    ? (
        !txMarkedMissingCostBasis &&
        hasNumericFiatValue(
          pickFiatTaxable(saleTx) ?? saleTx?.taxableProfitEUR ?? saleTx?.taxableProfit
        )
          ? Number(
              pickFiatTaxable(saleTx) ?? saleTx?.taxableProfitEUR ?? saleTx?.taxableProfit
            )
          : null
      )
    : (
        taxStatusKnown && hasNumericFiatValue(grossProfitValue)
          ? (
              isUK || selectedCountry === "GR"
                ? grossProfitValue
                : (isManuallyNonTaxable ? 0 : grossProfitValue)
            )
          : null
      );

  const showCostPlaceholder = txMarkedMissingPrice || !displayEinstandKnown;
  const showSalePlaceholder = txMarkedMissingPrice || !saleValueKnown;
  const showGrossPlaceholder =
    backendTaxBlocked || txMarkedMissingPrice || !hasNumericFiatValue(grossProfitValue);
  const showTaxablePlaceholder =
    txMarkedMissingPrice ||
    !taxStatusKnown ||
    !hasNumericFiatValue(finalSteuerGewinnEUR);

  const shouldShowMissingCostBasisWarning =
    txMarkedMissingCostBasis &&
    !hasManualEinstand;

  const currencySymbol = currencySymbolOf(fiatCurrency);
  const formatEuro = (value) => `${currencySymbol} ${fmtEurRow(value)}`;
  const pdfMiniWrap = "mt-1 border border-black rounded-none p-[0.7mm]";
  const pdfMiniTitle = "text-[6.7pt] leading-[1.12] font-semibold text-black";
  const pdfMiniText = "text-[6.7pt] leading-[1.12] text-black";
  const pdfMiniValue = "text-[6.7pt] leading-[1.12] font-semibold text-black";
  const pdfMiniBadge = "text-[6.7pt] leading-[1.12] font-semibold text-black";
  const pdfHeadCell =
    "px-[0.55mm] py-[0.45mm] border border-black text-[6.7pt] leading-[1.12] font-semibold text-black text-center bg-white";
  const pdfBodyCell =
    "px-[0.55mm] py-[0.45mm] border border-black text-[6.7pt] leading-[1.12] text-black text-center align-middle bg-white";
  const pdfBodyCellUsed =
    "px-[0.55mm] py-[0.45mm] border border-black text-[6.7pt] leading-[1.12] text-black text-center align-middle bg-white";
  const pdfTable = "w-full border border-black border-collapse table-auto";
  const pdfNotice =
    "mb-1 border border-black rounded-none px-[0.55mm] py-[0.45mm] text-[6.7pt] leading-[1.12] text-black bg-white";

  const displayEinstand = finalEinstandEUR;

  return (
    <tr className="sale-detail-row pdf-tx-detail-row bg-gray-50 print:bg-white print:break-inside-avoid print:page-break-inside-avoid print:break-before-avoid-page print:page-break-before-avoid">
      <td colSpan={colSpan} className="p-2 border print:pt-0 print:pb-2 print:pl-3 print:pr-2 print:break-inside-auto print:page-break-inside-auto">
        <div className="mt-1">
          <div className="font-semibold mb-1">{saleDetailTitle}:</div>
          
          {/* 🔧 NEU: Warnung / fehlender NFT-Einstand */}
          {shouldShowMissingCostBasisWarning ? (
            <div className={pdfExportMode ? pdfNotice : "mb-2 p-2 bg-sky-100 border border-sky-300 rounded text-sky-800 text-xs"}>
              {language === 'en'
                ? 'NFT sale detected, but no acquisition cost basis was found. The sale is shown, but profit/loss is excluded until purchase price and acquisition date are added manually.'
                : 'NFT-Verkauf erkannt, aber kein NFT-Einstand gefunden. Der Verkauf wird angezeigt, Gewinn/Verlust jedoch erst nach manueller Ergänzung von Einstand und Kaufdatum einbezogen.'}
            </div>
          ) : (
            !hasBackend &&
            warningMessage &&
            !(manualNftData[saleKey]?.einstand || manualNftData[saleKey]?.kaufDatum) && (
              <div className={pdfExportMode ? pdfNotice : "mb-2 p-2 bg-yellow-100 border border-yellow-300 rounded text-yellow-800 text-xs"}>
                {warningMessage}
              </div>
            )
          )}

          {/* 🔧 NEU: Bemerkung wenn manuelle Daten eingegeben wurden */}
          {(manualNftData[saleKey]?.einstand || manualNftData[saleKey]?.kaufDatum) && (
            <div className={pdfExportMode ? pdfNotice : "mb-2 p-2 bg-green-100 border border-green-300 rounded text-green-800 text-xs"}>
              ✅ Werte wurden manuell eingegeben
            </div>
          )}

          <table className={pdfExportMode ? pdfTable : "text-xs w-full border border-gray-300 border-collapse table-fixed"}>
            {pdfExportMode && (
              <colgroup>
                <col style={{ width: "20%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
            )}
            <thead>
              <tr className={pdfExportMode ? "bg-white text-black font-semibold" : "bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 font-semibold"}>
                <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-300 w-[14%] text-center"}>
                  <div className="flex items-center justify-center gap-1">
                    {showIcons && <span className="text-xs">📊</span>}
                    <span>{t('used', language)}</span>
                  </div>
                </th>
                <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[16%] text-center print-nowrap"}>
                  <div className="flex items-center justify-center gap-1">
                    {showIcons && <span className="text-xs">📅</span>}
                    <span>{t('receivedOn', language)}</span>
                  </div>
                </th>
                <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[14%] text-center"}>
                  <div className="flex items-center justify-center gap-1">
                    {showIcons && <span className="text-xs">⏱️</span>}
                    <span>
                      {isUK
                        ? (language === "en" ? "UK treatment" : "UK-Behandlung")
                        : t("holdingDuration", language)}
                    </span>
                  </div>
                </th>
                <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[14%] text-center"}>
                  <div className="flex items-center justify-center gap-1">
                    {showIcons && <span className="text-xs">💰</span>}
                    <span>{fiatColumnLabelFromEurKey('purchasePriceEUR', fiatCurrency, language, t)}</span>
                  </div>
                </th>
                <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[14%] text-center"}>
                  <div className="flex items-center justify-center gap-1">
                    {showIcons && <span className="text-xs">💸</span>}
                    <span>{fiatColumnLabelFromEurKey('salePriceEUR', fiatCurrency, language, t)}</span>
                  </div>
                </th>
                <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[14%] text-center"}>
                  <div className="flex items-center justify-center gap-1">
                    {showIcons && <span className="text-xs">📈</span>}
                    <span>{fiatColumnLabelFromEurKey('profitEUR', fiatCurrency, language, t)}</span>
                  </div>
                </th>
                <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[10%] text-center"}>
                  <div className="flex items-center justify-center gap-1">
                    {showIcons && <span className="text-xs">⚖️</span>}
                    <span>{t('taxable', language)}</span>
                  </div>
                </th>
                {shouldShowInputFields && selectedCountry !== "GR" && !isUK && (
                  <th className={pdfExportMode ? pdfHeadCell : "p-2 border border-blue-200 w-[10%] text-center"}>
                    <div className="flex items-center justify-center gap-1">
                      {showIcons && <span className="text-xs">🕐</span>}
                      <span>{t('holdingPeriodOver365Days', language)}</span>
                    </div>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white hover:bg-blue-50 transition-colors duration-200 text-center">
                <td className={pdfExportMode ? pdfBodyCellUsed : "p-2 border border-gray-200 text-center"}>
                  <div className="flex items-center justify-center gap-1">
                    <span className="font-medium text-gray-900">1 × {saleTx.nftName || "NFT"}</span>
                    {!pdfExportMode && <span className="text-blue-400">🖼</span>}
                  </div>
                </td>
                
                {/* 🔧 NEU: Kaufdatum/Empfangsdatum */}
                <td className={pdfExportMode ? pdfBodyCell : "p-2 border border-gray-200 text-center font-medium text-gray-800"}>
                  {shouldShowInputFields ? (
                    <input
                      type="date"
                      value={manualKaufDatum}
                      onChange={(e) => setManualKaufDatum(e.target.value)}
                      className="w-full text-xs p-2 border rounded focus:ring-2 focus:ring-blue-300 focus:border-blue-500"
                    />
                  ) : (
                    displayKaufDatum
                      ? typeof displayKaufDatum === "string"
                        ? displayKaufDatum
                        : (pdfExportMode
                            ? formatDateTimeUtcFromTimestamp(displayKaufDatum.getTime())
                            : formatDateFromTimestamp(displayKaufDatum.getTime()))
                      : "—"
                  )}
                </td>
                
                {/* 🔧 NEU: Haltedauer */}
                <td className={pdfExportMode ? pdfBodyCell : "p-2 border border-gray-200 text-center"}>
                  {shouldShowInputFields ? (
                    <span className="text-gray-500">Auto-berechnet</span>
                  ) : (
                    finalHaltedauerStr !== '—' ? (
                      pdfExportMode ? (
                        <span className="text-black">{finalHaltedauerStr}</span>
                      ) : (
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium print-nowrap pdf-badge">
                          {finalHaltedauerStr}
                        </span>
                      )
                    ) : (
                      "—"
                    )
                  )}
                </td>
                
                {/* 🔧 NEU: Einstandspreis */}
                <td className={pdfExportMode ? pdfBodyCell : "p-2 border border-gray-200 text-center font-medium text-gray-800"}>
                  {shouldShowInputFields ? (
                    <input
                      type="number"
                      step="0.01"
                      value={manualEinstand}
                      onChange={(e) => setManualEinstand(e.target.value)}
                      placeholder={`${currencySymbol} 0.00`}
                      className="w-full text-xs p-2 border rounded focus:ring-2 focus:ring-blue-300 focus:border-blue-500"
                    />
                  ) : (
                    maskDetailEur ? (
                      <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} showLock={!pdfExportMode} dense fullWidth />
                    ) : backendTaxBlocked ? (
                      <span className="font-mono text-amber-800 font-medium">
                        {language === "en" ? "Unknown" : "Unbekannt"}
                      </span>
                    ) : showCostPlaceholder ? (
                      <span className="font-mono text-slate-400">—</span>
                    ) : (
                      formatEuro(displayEinstand)
                    )
                  )}
                </td>

                {/* 💸 Verkaufspreis */}
                <td className={pdfExportMode ? pdfBodyCell : "p-2 border border-gray-200 text-center font-medium text-gray-800"}>
                  {maskDetailEur ? (
                    <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} dense fullWidth />
                  ) : showSalePlaceholder ? (
                    <span className="font-mono text-slate-400">—</span>
                  ) : (
                    formatEuro(finalSaleEUR)
                  )}
                </td>

                {/* 📈 Gewinn (brutto) */}
                <td className={pdfExportMode ? pdfBodyCell : "p-2 border border-gray-200 text-center"}>
                  {maskDetailEur ? (
                    <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} showLock={!pdfExportMode} dense fullWidth />
                  ) : showGrossPlaceholder ? (
                    <span className="font-mono text-slate-400">—</span>
                  ) : pdfExportMode ? (
                    <span className={pdfMiniValue}>{formatEuro(grossProfitValue)}</span>
                  ) : (
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        grossProfitValue >= 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                      } print-nowrap pdf-badge`}
                    >
                      {formatEuro(grossProfitValue)}
                    </span>
                  )}
                </td>
                
                {/* 🔧 NEU: Steuerpflichtig */}
                <td className={pdfExportMode ? pdfBodyCell : "p-2 border border-gray-200 text-center font-semibold"}>
                  {backendTaxBlocked ? (
                    pdfExportMode ? (
                      <span className={pdfMiniText}>{language === "en" ? "Review" : "Prüfen"}</span>
                    ) : (
                      <span className="px-2 py-1 bg-amber-100 text-amber-900 rounded-full text-xs font-medium print-nowrap pdf-badge">
                        {language === "en" ? "Review" : "Prüfen"}
                      </span>
                    )
                  ) : isUK ? (
                    pdfExportMode ? (
                      <span className="text-black">{language === "en" ? "Included" : "Enthalten"}</span>
                    ) : (
                      <span
                        className="px-2 py-1 bg-sky-100 text-sky-800 rounded-full text-xs font-medium print-nowrap pdf-badge"
                        title={
                          language === "en"
                            ? "NFTs are identifiable assets in UK. No one-year exemption applies."
                            : "NFTs sind in UK einzeln identifizierbare Assets. Keine 1-Jahres-Steuerfreiheit."
                        }
                      >
                        {language === "en" ? "Included" : "Enthalten"}
                      </span>
                    )
                  ) : finalSteuerpflichtig === null ? (
                    pdfExportMode ? (
                      <span className={pdfMiniText}>{language === 'en' ? 'Not evaluated' : 'Nicht bewertet'}</span>
                    ) : (
                      <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium print-nowrap pdf-badge">
                        {language === 'en' ? 'Not evaluated' : 'Nicht bewertet'}
                      </span>
                    )
                  ) : finalSteuerpflichtig ? (
                    pdfExportMode ? (
                      <span className="text-black">{t('yes', language)}</span>
                    ) : (
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium print-nowrap pdf-badge">
                        ✅ {t('yes', language)}
                      </span>
                    )
                  ) : pdfExportMode ? (
                    <span className="text-black">{t('no', language)}</span>
                  ) : (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium print-nowrap pdf-badge">
                      ❌ {t('no', language)}
                    </span>
                  )}
                </td>
                
                {/* 🔧 NEU: Checkbox für manuelle Markierung */}
                {shouldShowInputFields && selectedCountry !== "GR" && !isUK && (
                  <td className="p-2 border border-gray-200 text-center">
                    <label className="flex items-center justify-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isManuallyNonTaxable}
                        onChange={(e) => handleNonTaxableChange(e.target.checked)}
                        className="mr-1 w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        title={t('markAsNonTaxable', language)}
                      />
                      <span className="text-xs text-blue-600">
                        {isManuallyNonTaxable ? "✅" : "🕐"}
                      </span>
                    </label>
                  </td>
                )}
              </tr>
            </tbody>
          </table>

          {/* 💼 KOMPAKTE GEWINN-ZUSAMMENFASSUNG - wie bei FifoDetailRow */}
          <div className={pdfExportMode ? pdfMiniWrap : "mt-3 bg-white border border-gray-200 rounded-md p-3"}>
            <div className="flex items-center justify-between mb-2">
              <span className={pdfExportMode ? pdfMiniTitle : "text-sm font-medium text-gray-700"}>
                {language === 'en' ? 'Profit Analysis' : 'Gewinnanalyse'}
              </span>
              <span
                className={
                  pdfExportMode
                    ? pdfMiniBadge
                    : `px-2 py-0.5 rounded-full text-xs font-medium print-nowrap pdf-badge ${
                        backendTaxBlocked
                          ? 'bg-amber-100 text-amber-900'
                          : finalSteuerpflichtig === null
                            ? 'bg-slate-100 text-slate-700'
                            : finalSteuerpflichtig
                              ? 'bg-red-100 text-red-700'
                              : 'bg-green-100 text-green-700'
                      }`
                }
              >
                {backendTaxBlocked
                  ? blockedTaxLabel(saleTx, language)
                  : isUK
                    ? (language === "en" ? "UK CGT basis" : "UK CGT-Basis")
                    : finalSteuerpflichtig === null
                      ? (language === 'en' ? 'Not Included' : 'Nicht einbezogen')
                      : finalSteuerpflichtig
                        ? (language === 'en' ? 'Taxable' : 'Steuerpflichtig')
                        : (language === 'en' ? 'Tax Free' : 'Steuerfrei')
                }
              </span>
            </div>
            
            <div className="space-y-1">
              {backendTaxBlocked && (
                <div className={pdfExportMode ? pdfMiniText : "text-xs text-amber-800 mb-1"}>
                  <div>
                    <strong>{language === "en" ? "Gross profit:" : "Bruttogewinn:"}</strong>{" "}
                    {language === "en" ? "Not calculated" : "Nicht berechnet"}
                  </div>
                  <div>
                    <strong>{language === "en" ? "Taxable profit:" : "Steuerpflichtiger Gewinn:"}</strong>{" "}
                    {language === "en" ? "Not calculated" : "Nicht berechnet"}
                  </div>
                  {Number(saleTx?.fifoShortageQty ?? saleTx?.missingFifoQty ?? 0) > 0 && (
                    <div className="mt-0.5">
                      {language === "en" ? "Missing FIFO quantity:" : "Fehlende FIFO-Menge:"}{" "}
                      <strong>
                        {fmtTokenAmount(
                          Number(saleTx?.fifoShortageQty ?? saleTx?.missingFifoQty ?? 0),
                        )}{" "}
                        {saleTx?.token || saleTx?.symbol || "VTHO"}
                      </strong>
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className={pdfExportMode ? pdfMiniText : "text-sm text-gray-600"}>
                  {showGrossPlaceholder
                    ? (language === 'en' ? 'Gross Result' : 'Bruttoergebnis')
                    : (grossProfitValue >= 0
                        ? (language === 'en' ? 'Gross Profit' : 'Bruttogewinn')
                        : (language === 'en' ? 'Gross Loss' : 'Bruttoverlust'))
                  }
                </span>
                {maskSummaryEur ? (
                  <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} dense />
                ) : showGrossPlaceholder ? (
                  <span className="font-mono text-slate-400">—</span>
                ) : (
                  <span
                    className={
                      pdfExportMode
                        ? pdfMiniValue
                        : `px-2 py-0.5 rounded-full text-xs font-semibold ${
                            grossProfitValue >= 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                          } print-nowrap pdf-badge`
                    }
                  >
                    {formatEuro(grossProfitValue)}
                  </span>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className={pdfExportMode ? pdfMiniText : "text-sm text-gray-600"}>
                  {showTaxablePlaceholder
                    ? (language === 'en' ? 'Taxable Result' : 'Steuerliches Ergebnis')
                    : finalSteuerGewinnEUR >= 0
                      ? (language === 'en' ? 'Taxable Profit' : 'Steuerpflichtiger Gewinn')
                      : (language === 'en' ? 'Taxable Loss' : 'Steuerpflichtiger Verlust')
                  }
                </span>
                {maskSummaryEur ? (
                  <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} dense />
                ) : showTaxablePlaceholder ? (
                  <span className="font-mono text-slate-400">—</span>
                ) : (
                  <span
                    className={
                      pdfExportMode
                        ? pdfMiniValue
                        : `px-2 py-0.5 rounded-full text-xs font-semibold ${
                            finalSteuerGewinnEUR >= 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                          } print-nowrap pdf-badge`
                    }
                  >
                    {formatEuro(finalSteuerGewinnEUR)}
                  </span>
                )}
              </div>
            </div>
            
            <div className={pdfExportMode ? "mt-1 pt-1 border-t border-black" : "mt-2 pt-2 border-t border-gray-200"}>
              <div className={pdfExportMode ? `${pdfMiniText} text-right` : "text-sm text-gray-600 text-right"}>
                {language === 'en' ? 'Cost' : 'Einstand'}: <span className="font-mono">{maskSummaryEur ? <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} dense /> : showCostPlaceholder ? <span className="text-slate-400">—</span> : formatEuro(displayEinstand)}</span> • 
                {language === 'en' ? 'Sale' : 'Verkauf'}: <span className="font-mono">{maskSummaryEur ? <MaskedEurCell currencySymbol={currencySymbol} currencyCode={fiatCurrency} language={language} dense /> : showSalePlaceholder ? <span className="text-slate-400">—</span> : formatEuro(finalSaleEUR)}</span>
              </div>
            </div>
          </div>

          {/* 🔧 NEU: Tipp für manuelle Eingabe */}
          {shouldShowInputFields && selectedCountry !== 'GR' && (
            <div className="mt-2 text-xs text-blue-600 italic">
              💡 {t('tip', language)}: {t('markNftSalesAsNonTaxable', language)}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}